import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface EntraTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  tokenType: string;
  scope: string;
  expiresIn: number;
  extExpiresIn: number;
  accessNumber: string;
}

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'entra_access_token',
  ID_TOKEN: 'entra_id_token',
  REFRESH_TOKEN: 'entra_refresh_token',
  TOKEN_EXPIRY: 'entra_token_expiry',
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasValidToken());
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private isRefreshing = false;

  constructor(private http: HttpClient) {}

  /**
   * Exchange authorization code for tokens
   */
  exchangeCodeForTokens(code: string): Observable<EntraTokens> {
    const url = `${environment.apiUrl}/entra/oauth-callback`;
    return this.http.get<EntraTokens>(url, { params: { code } }).pipe(
      tap((tokens) => this.storeTokens(tokens)),
      catchError((error) => {
        console.error('Failed to exchange code for tokens:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Refresh the access token using the refresh token
   */
  refreshAccessToken(): Observable<EntraTokens> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    if (this.isRefreshing) {
      return throwError(() => new Error('Token refresh already in progress'));
    }

    this.isRefreshing = true;

    const url = `${environment.apiUrl}/entra/refresh-token`;
    return this.http.put<EntraTokens>(url, { refreshToken }).pipe(
      tap((tokens) => {
        this.storeTokens(tokens);
        this.isRefreshing = false;
      }),
      catchError((error) => {
        this.isRefreshing = false;
        this.clearTokens();
        console.error('Failed to refresh token:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Store tokens in localStorage
   */
  storeTokens(tokens: EntraTokens): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(STORAGE_KEYS.ID_TOKEN, tokens.idToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);

    // Calculate and store expiry time
    const expiryTime = Date.now() + tokens.expiresIn * 1000;
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());

    this.isAuthenticatedSubject.next(true);
  }

  /**
   * Clear all tokens from localStorage
   */
  clearTokens(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
    this.isAuthenticatedSubject.next(false);
  }

  /**
   * Get access token from localStorage
   */
  getAccessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  /**
   * Get ID token from localStorage
   */
  getIdToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ID_TOKEN);
  }

  /**
   * Get refresh token from localStorage
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Check if user has a valid (non-expired) token
   */
  hasValidToken(): boolean {
    const accessToken = this.getAccessToken();
    const expiryTime = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);

    if (!accessToken || !expiryTime) {
      return false;
    }

    // Check if token is expired (with 60 second buffer)
    return Date.now() < parseInt(expiryTime, 10) - 60000;
  }

  /**
   * Check if token needs refresh (within 5 minutes of expiry)
   */
  tokenNeedsRefresh(): boolean {
    const expiryTime = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);

    if (!expiryTime) {
      return false;
    }

    // Refresh if within 5 minutes of expiry
    return Date.now() > parseInt(expiryTime, 10) - 5 * 60 * 1000;
  }

  /**
   * Redirect to Entra login
   */
  redirectToLogin(): void {
    window.location.href = `${environment.apiUrl}/entra/login`;
  }

  /**
   * Redirect to Entra logout
   */
  logout(): void {
    this.clearTokens();
    window.location.href = `${environment.apiUrl}/entra/logout`;
  }
}
